import EntityLabel from "../../components/EntityLabel";
import { useWarehouse } from "../../warehouse/WarehouseContext";
import ManagementPageHTML, {
  IdentityCell,
  StatusBadge,
} from "../../components/ManagementPage/ManagementPageHTML";
import type {
  ManagementColumn,
  ManagementDialogField,
} from "../../components/ManagementPage/ManagementPageTypes";
import type { InventoryMovement } from "../../models/InventoryMovement";
import { InventoryMovementService } from "../../services/InventoryMovementService";
const types = [
  "StockIn",
  "StockOut",
  "Sale",
  "Adjustment",
  "Return",
  "Damage",
  "Expired",
  "Cancellation",
];
const columns: ManagementColumn<InventoryMovement>[] = [
  {
    Key: "Movement",
    Header: "Movement",
    Render: (row) => (
      <IdentityCell
        Icon="fa-right-left"
        Title={<EntityLabel entity="product" code={row.ProductCode} />}
        Subtitle={row.Code}
      />
    ),
  },
  {
    Key: "Type",
    Header: "Type",
    Render: (row) => <StatusBadge Status={row.Type} />,
  },
  {
    Key: "Quantity",
    Header: "Quantity (base units)",
    Render: (row) => <strong>{row.Quantity}</strong>,
  },
  {
    Key: "Change",
    Header: "Stock change",
    Render: (row) => `${row.PreviousQuantity} → ${row.NewQuantity}`,
  },
  { Key: "Reason", Header: "Reason", Render: (row) => row.Reason || "—" },
  {
    Key: "Reference",
    Header: "Reference",
    Render: (row) => row.ReferenceCode || "—",
  },
];
const fields: ManagementDialogField<InventoryMovement>[] = [
  { Key: "Code", Label: "Movement code", DisableAfterNew: true },
  { Key: "ProductCode", Label: "Product code", Required: true },
  {
    Key: "Type",
    Label: "Movement type",
    Type: "select",
    Options: types,
    Required: true,
  },
  { Key: "Quantity", Label: "Quantity", Type: "decimal", Required: true },
  {
    Key: "PreviousQuantity",
    Label: "Previous quantity",
    Type: "decimal",
    Required: true,
  },
  {
    Key: "NewQuantity",
    Label: "New quantity",
    Type: "decimal",
    Required: true,
  },
  { Key: "ReferenceCode", Label: "Reference" },
  { Key: "Reason", Label: "Reason", Type: "textarea", FullWidth: true },
];
export default function InventoryMovementHTML() {
  const { Warehouse } = useWarehouse();
  return (
    <ManagementPageHTML
      CanExport={false}
      Service={InventoryMovementService(Warehouse?.Code)}
      DefaultValues={{ WareHouse: Warehouse?.Code }}
      CanCreate={false}
      CanEdit={false}
      CanDelete={false}
      Title="Stock movement"
      Eyebrow="Inventory activity"
      Description="Quantities are in product base stock units. Review the inventory audit trail for purchases, sales, returns and adjustments."
      AddLabel="Record movement"
      SearchPlaceholder="Search product or reference"
      Rows={[]}
      Columns={columns}
      DialogFields={fields}
      Statuses={types}
      GetStatus={(row) => row.Type}
      GetRowKey={(row) => row.Code}
      GetSearchText={(row) =>
        `${row.Code} ${row.ProductCode} ${row.ReferenceCode ?? ""}`
      }
      Summaries={[]}
      GetSummaries={(rows, total) => [
        {
          Label: "Movements",
          Value: total,
          Icon: "fa-right-left",
          Tone: "Blue",
        },
        {
          Label: "Stock in on this page",
          Value: rows
            .filter((x) => x.Type === "StockIn")
            .reduce((s, x) => s + x.Quantity, 0),
          Icon: "fa-arrow-down",
          Tone: "Green",
        },
        {
          Label: "Stock out on this page",
          Value: rows
            .filter((x) => ["Sale", "StockOut", "Damage", "Expired"].includes(x.Type))
            .reduce((s, x) => s + x.Quantity, 0),
          Icon: "fa-arrow-up",
          Tone: "Red",
        },
        {
          Label: "Adjustments on this page",
          Value: rows.filter((x) => x.Type === "Adjustment").length,
          Icon: "fa-sliders",
          Tone: "Orange",
        },
      ]}
    />
  );
}
